import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { getMobilePreviewPolicy, getPlatformPreviewMeta } from "@/lib/postReviewWorkspace";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { PostPreview } from "@/components/content/PostPreview";
import { PLATFORM_LOGO } from "@/components/brand/PlatformLogos";

const EMPTY_ASSET = {};

// Brand colors tuned to read on the dark preview header (X/Threads are near-black
// brands, so their "color" here is a light neutral so the name stays legible).
const PLATFORM_COLOR = {
  linkedin: "#4DA3F0",
  x: "#E7E9EA",
  twitter: "#E7E9EA",
  facebook: "#5B9BFF",
  instagram: "#F0699E",
  threads: "#E7E9EA",
  bluesky: "#4CA9FF",
  youtube: "#FF5C5C",
  youtube_shorts: "#FF5C5C",
};

export function PlatformPreviewPanel({
  asset,
  className,
  previewClassName,
  bodyClassName,
  title,
  note,
  headerAction,
  footer,
  showStatus = true,
  compact = false,
  dark = false,
  mobilePreviewContext,
}) {
  const safeAsset = asset || EMPTY_ASSET;
  const meta = getPlatformPreviewMeta(safeAsset);
  const platformKey = safeAsset.platform === "twitter" ? "x" : safeAsset.platform;
  const PlatformLogo = PLATFORM_LOGO[platformKey];
  const brandColor = PLATFORM_COLOR[platformKey] || (dark ? "#9BF3FF" : "#0A66C2");
  const mobilePolicy = useMemo(
    () => (mobilePreviewContext ? getMobilePreviewPolicy(safeAsset, mobilePreviewContext) : { collapsible: false }),
    [safeAsset, mobilePreviewContext],
  );
  const [mobileExpanded, setMobileExpanded] = useState(Boolean(mobilePolicy.defaultExpanded));

  useEffect(() => {
    setMobileExpanded(Boolean(mobilePolicy.defaultExpanded));
  }, [safeAsset.id, mobilePreviewContext, mobilePolicy.defaultExpanded]);

  if (!asset) return null;

  const clampMobile = mobilePolicy.collapsible && !mobileExpanded;

  return (
    <section
      className={cn(
        "overflow-hidden rounded-xl border shadow-[0_18px_42px_-34px_rgba(18, 22, 20,0.45)]",
        dark ? "bg-[#0C0F0E] text-white" : "bg-slate-100/85 text-slate-950",
        className,
      )}
      style={{ borderColor: dark ? "rgba(150, 160, 152,0.20)" : "rgba(160, 168, 162,0.92)" }}
      data-testid="platform-preview-panel"
    >
      <header
        className={cn(
          "flex flex-wrap items-start justify-between gap-3 border-b",
          compact ? "px-3 py-2.5" : "px-4 py-3",
          dark ? "border-white/10 bg-white/[0.045]" : "border-slate-200/90 bg-white/80",
        )}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border"
            style={{ color: brandColor, backgroundColor: `${brandColor}1F`, borderColor: `${brandColor}55` }}
          >
            {PlatformLogo ? <PlatformLogo size={22} /> : <Eye className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <h3 className={cn("truncate font-bold tracking-[-0.01em]", compact ? "text-[15px]" : "text-base")} style={{ color: brandColor }}>
              {meta.platformLabel}
            </h3>
            <p className={cn("truncate text-[10px] font-semibold uppercase tracking-[0.16em]", dark ? "text-slate-400" : "text-slate-500")}>
              {title || meta.previewNote || "Native preview"}
            </p>
            {note && !compact && (
              <p className={cn("mt-1 text-[12px] leading-relaxed", dark ? "text-slate-300" : "text-slate-600")}>{note}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {showStatus && asset.status ? <StatusBadge status={asset.status} showIcon={!compact} /> : null}
          {headerAction}
        </div>
      </header>

      <div className={cn(compact ? "p-3" : "p-4", dark ? "bg-slate-900/60" : "bg-slate-100/80", bodyClassName)}>
        <div className="mx-auto w-full max-w-3xl">
          <div
            className={cn(
              "relative",
              clampMobile && "max-h-[var(--mobile-preview-height)] overflow-hidden sm:max-h-none sm:overflow-visible",
              previewClassName,
            )}
            style={clampMobile ? { "--mobile-preview-height": `${mobilePolicy.collapsedHeight}px` } : undefined}
          >
            <PostPreview asset={asset} />
            {clampMobile && (
              <span
                aria-hidden
                className={cn(
                  "pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t sm:hidden",
                  dark ? "from-slate-900 via-slate-900/88 to-transparent" : "from-slate-100 via-slate-100/88 to-transparent",
                )}
              />
            )}
          </div>
          {mobilePolicy.collapsible && (
            <button
              type="button"
              onClick={() => setMobileExpanded((current) => !current)}
              className={cn(
                "mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-[12px] font-semibold sm:hidden",
                dark
                  ? "border-white/12 bg-white/[0.08] text-[color:var(--text-1)] hover:bg-white/12"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
              )}
              data-testid="platform-preview-mobile-toggle"
            >
              {mobileExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {mobileExpanded ? mobilePolicy.collapseLabel : mobilePolicy.expandLabel}
            </button>
          )}
        </div>
      </div>

      <footer
        className={cn(
          "flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2.5 text-[11.5px]",
          dark ? "border-white/10 bg-white/[0.035] text-slate-300" : "border-slate-200/90 bg-white/80 text-slate-500",
        )}
      >
        <span>{meta.characterLabel}</span>
        {meta.overLimit ? (
          <span className="inline-flex items-center gap-1 text-red-500">
            <AlertTriangle className="h-3.5 w-3.5" /> Over limit
          </span>
        ) : (
          <span className={dark ? "text-[color:var(--text-1)]/75" : "text-slate-500"}>{meta.surfaceLabel}</span>
        )}
      </footer>

      {footer ? (
        <div className={cn("border-t px-4 py-3", dark ? "border-white/10 bg-[#0C0F0E]" : "border-slate-200/90 bg-white")}>
          {footer}
        </div>
      ) : null}
    </section>
  );
}
